import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Check } from "lucide-react";

import { InvoiceDetailLink } from "@/components/commissions/InvoiceDetailLink";
import { QboMvMismatchBadge } from "@/components/commissions/QboMvMismatchBadge";
import { buildInvoiceDetailPeekContext } from "./invoiceDetailPeek";
import { EndUserDetailLink } from "@/components/inside-sales/EndUserDetailLink";
import { RepAgencyDetailLink } from "@/components/inside-sales/RepAgencyDetailLink";
import { HubDataTable, type HubDataTableColumn } from "@/components/hub/HubDataTable";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input, Label } from "@/components/ui/Input";
import { PageTitleWithHelp, HoverHintContent } from "@/components/ui/title-help";
import { cn } from "@/lib/utils";
import {
  BONUS_INVOICE_STATUS_OPTIONS,
  COMMISSION_INVOICE_STATUS_OPTIONS,
  DEFAULT_BONUS_STATUS_FILTER,
  DEFAULT_COMMISSION_STATUS_FILTER,
  commissionsStableListQueryOptions,
  listCommissionInvoiceStatuses,
  type BonusInvoiceStatus,
  type CommissionInvoiceStatus,
  type CommissionInvoiceStatusSortDir,
  type CommissionInvoiceStatusSortKey,
} from "./commissionsApi";

const PAGE_SIZE = 50;

type InvoiceStatusRow = Awaited<ReturnType<typeof listCommissionInvoiceStatuses>>["rows"][number];

function formatDate(value: string | null): string {
  if (value == null || value.trim() === "") {
    return "—";
  }
  const parsed = new Date(`${value.slice(0, 10)}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function statusBadgeTone(
  status: CommissionInvoiceStatus | BonusInvoiceStatus,
): "neutral" | "info" | "success" | "warning" {
  switch (status) {
    case "vendor_invoice_paid":
      return "success";
    case "vendor_invoice_issued":
    case "on_statement":
      return "info";
    case "ready_for_statement":
      return "warning";
    case "not_applicable":
      return "neutral";
    case "zero_dollar_gift":
      return "success";
    default:
      return "neutral";
  }
}

const COMMISSION_JE_POSTED_HOVER_HINT =
  "Checkmark when the hub posted the GOSI commission accrual journal entry to QuickBooks Online (gosi_commission_journal_entry sync_status = posted). Empty when no posted entry exists yet.";

const NEW_END_USER_BONUS_HISTORICAL_HOVER_HINT =
  "Checkmark when the sales-order date fell inside a new-end-user bonus window (manual profile dates or the 2-year gap rule). Eligibility uses sales-order date, not GOSI invoice date.";

function QboJournalPostedIndicator({ posted }: { posted: boolean }) {
  if (!posted) {
    return <span className="text-slate-300" aria-hidden>—</span>;
  }
  return (
    <Check
      className="mx-auto h-4 w-4 text-emerald-600"
      aria-label="Commission journal entry posted to QuickBooks"
    />
  );
}

function NewEndUserBonusHistoricalIndicator({ inEffect }: { inEffect: boolean }) {
  if (!inEffect) {
    return <span className="text-slate-300" aria-hidden>—</span>;
  }
  return (
    <Check
      className="mx-auto h-4 w-4 text-emerald-600"
      aria-label="New end user bonus was in effect at sales-order date"
    />
  );
}

function StatusBadge({
  status,
  options,
}: {
  status: CommissionInvoiceStatus | BonusInvoiceStatus;
  options: Array<{ value: string; label: string }>;
}) {
  const label = options.find((option) => option.value === status)?.label ?? status;
  return <Badge tone={statusBadgeTone(status)}>{label}</Badge>;
}

function filterTogglePillClass(active: boolean): string {
  return cn(
    "rounded-full border px-3 py-1 text-xs font-medium transition",
    active
      ? "border-brand-500 bg-brand-50 text-brand-800"
      : "border-slate-300 bg-white text-slate-500 hover:border-slate-400",
  );
}

function statusFilterKey(values: Iterable<string>): string {
  return Array.from(values).sort().join("|");
}

const INVOICE_STATUS_COLUMNS: HubDataTableColumn<
  InvoiceStatusRow,
  CommissionInvoiceStatusSortKey
>[] = [
  {
    id: "invoice_number",
    header: "Invoice",
    sortKey: "invoice_number",
    overflow: "nowrap",
    sortValue: (row) => row.invoice_number ?? "",
    cell: (row) => (
      <div className="flex items-center gap-1.5">
        <InvoiceDetailLink
          invoiceMvId={row.invoice_mv_id}
          invoiceNumber={row.invoice_number}
          peek={buildInvoiceDetailPeekContext(row)}
        />
        {row.qb_line_total_mismatch ? (
          <QboMvMismatchBadge
            megaventoryTotal={row.total_amount}
            quickbooksTotal={row.qb_compared_total}
            reason={row.qb_line_total_mismatch_reason}
          />
        ) : null}
      </div>
    ),
  },
  {
    id: "invoice_date",
    header: "Date",
    sortKey: "invoice_date",
    overflow: "nowrap",
    sortValue: (row) => row.invoice_date ?? "",
    cell: (row) => formatDate(row.invoice_date),
  },
  {
    id: "commission_status",
    header: "Commission",
    sortKey: "commission_status",
    sortValue: (row) => row.commission_status,
    cell: (row) => (
      <StatusBadge status={row.commission_status} options={COMMISSION_INVOICE_STATUS_OPTIONS} />
    ),
  },
  {
    id: "bonus_status",
    header: "Bonus",
    sortKey: "bonus_status",
    sortValue: (row) => row.bonus_status,
    cell: (row) => <StatusBadge status={row.bonus_status} options={BONUS_INVOICE_STATUS_OPTIONS} />,
  },
  {
    id: "end_user_name",
    header: "End user",
    sortKey: "end_user_name",
    overflow: "truncate",
    sortValue: (row) => row.end_user_name ?? "",
    cell: (row) => (
      <EndUserDetailLink
        block
        fsAccountId={row.end_user_fs_account_id}
        endUserName={row.end_user_name}
      />
    ),
  },
  {
    id: "rep_agency_name",
    header: "Rep agency",
    sortKey: "rep_agency_name",
    sortValue: (row) => row.rep_agency_name ?? "",
    cell: (row) => (
      <RepAgencyDetailLink repAgencyId={row.rep_agency_id} repAgencyName={row.rep_agency_name} />
    ),
  },
  {
    id: "sales_order_year",
    header: "SO year",
    sortKey: "sales_order_year",
    align: "right",
    overflow: "nowrap",
    sortValue: (row) => row.sales_order_year ?? "",
    cell: (row) => row.sales_order_year ?? "—",
  },
  {
    id: "fully_paid_date",
    header: (
      <HoverHintContent
        hoverHint="QuickBooks customer payment date when the GOSI invoice was paid in full. Partial payments are ignored; dash until the invoice balance reaches zero."
        block
      >
        <span>Fully paid</span>
      </HoverHintContent>
    ),
    sortKey: "fully_paid_date",
    overflow: "nowrap",
    sortValue: (row) => row.fully_paid_date ?? "",
    cell: (row) => formatDate(row.fully_paid_date),
  },
  {
    id: "commission_journal_posted_to_qbo",
    header: (
      <HoverHintContent hoverHint={COMMISSION_JE_POSTED_HOVER_HINT} block>
        <span>QBO JE</span>
      </HoverHintContent>
    ),
    sortKey: "commission_journal_posted_to_qbo",
    sortValue: (row) => (row.commission_journal_posted_to_qbo ? 1 : 0),
    cell: (row) => (
      <div className="text-center">
        <QboJournalPostedIndicator posted={row.commission_journal_posted_to_qbo} />
      </div>
    ),
  },
  {
    id: "new_end_user_bonus_historically_in_effect",
    header: (
      <HoverHintContent hoverHint={NEW_END_USER_BONUS_HISTORICAL_HOVER_HINT} block>
        <span>NEU bonus</span>
      </HoverHintContent>
    ),
    sortKey: "new_end_user_bonus_historically_in_effect",
    sortValue: (row) => (row.new_end_user_bonus_historically_in_effect ? 1 : 0),
    cell: (row) => (
      <div className="text-center">
        <NewEndUserBonusHistoricalIndicator inEffect={row.new_end_user_bonus_historically_in_effect} />
      </div>
    ),
  },
  {
    id: "run_month",
    header: "Run month",
    sortKey: "run_month",
    overflow: "nowrap",
    sortValue: (row) => row.run_month ?? "",
    cell: (row) => (
      <>
        {row.run_month != null ? formatDate(row.run_month) : "—"}
        {row.run_status != null ? (
          <div className="text-xs text-slate-500">{row.run_status}</div>
        ) : null}
        {row.bonus_run_month != null ? (
          <div className="text-xs text-slate-500">
            Bonus: {formatDate(row.bonus_run_month)}
            {row.bonus_run_status != null ? ` · ${row.bonus_run_status}` : ""}
          </div>
        ) : null}
      </>
    ),
  },
  {
    id: "statement",
    header: "Statement",
    sortKey: "run_month",
    sortValue: (row) => row.statement_status ?? "",
    cell: (row) => (
      <>
        {row.statement_status ?? "—"}
        {row.bonus_statement_status != null ? (
          <div className="text-xs text-slate-500">Bonus: {row.bonus_statement_status}</div>
        ) : null}
      </>
    ),
  },
];

function StatusCountPills<T extends string>({
  title,
  options,
  counts,
  selected,
  onToggle,
  onReset,
  showReset,
}: {
  title: string;
  options: Array<{ value: T; label: string }>;
  counts: Partial<Record<string, number>>;
  selected: Set<T>;
  onToggle: (status: T) => void;
  onReset: () => void;
  showReset: boolean;
}) {
  return (
    <div className="space-y-2">
      <div className="flex min-h-8 flex-wrap items-center gap-2">
        <span className="text-xs font-medium uppercase tracking-wide text-slate-500">{title}</span>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={onReset}
          className={cn(!showReset && "pointer-events-none invisible")}
          tabIndex={showReset ? 0 : -1}
          aria-hidden={!showReset}
        >
          Reset filters
        </Button>
      </div>
      <div className="flex min-h-[4.5rem] flex-wrap content-start gap-1">
        {options.map((option) => {
          const active = selected.has(option.value);
          const count = counts[option.value] ?? 0;
          return (
            <button
              key={option.value}
              type="button"
              aria-pressed={active}
              onClick={() => onToggle(option.value)}
              className={filterTogglePillClass(active)}
            >
              {option.label}: {count.toLocaleString()}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function CommissionInvoiceStatusPage() {
  const [commissionStatusFilter, setCommissionStatusFilter] = useState<Set<CommissionInvoiceStatus>>(
    () => new Set(DEFAULT_COMMISSION_STATUS_FILTER),
  );
  const [bonusStatusFilter, setBonusStatusFilter] = useState<Set<BonusInvoiceStatus>>(
    () => new Set(DEFAULT_BONUS_STATUS_FILTER),
  );
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<CommissionInvoiceStatusSortKey>("invoice_date");
  const [sortDir, setSortDir] = useState<CommissionInvoiceStatusSortDir>("desc");
  const [offset, setOffset] = useState(0);

  const commissionStatusFilterKey = useMemo(
    () => statusFilterKey(commissionStatusFilter),
    [commissionStatusFilter],
  );
  const bonusStatusFilterKey = useMemo(() => statusFilterKey(bonusStatusFilter), [bonusStatusFilter]);
  const commissionStatuses = useMemo(
    () => Array.from(commissionStatusFilter),
    [commissionStatusFilter],
  );
  const bonusStatuses = useMemo(() => Array.from(bonusStatusFilter), [bonusStatusFilter]);

  useEffect(() => {
    setOffset(0);
  }, [commissionStatusFilterKey, bonusStatusFilterKey, search, sortKey, sortDir]);

  const toggleCommissionStatusFilter = useCallback((status: CommissionInvoiceStatus): void => {
    setCommissionStatusFilter((current) => {
      const next = new Set(current);
      if (next.has(status)) {
        next.delete(status);
      } else {
        next.add(status);
      }
      return next;
    });
  }, []);

  const toggleBonusStatusFilter = useCallback((status: BonusInvoiceStatus): void => {
    setBonusStatusFilter((current) => {
      const next = new Set(current);
      if (next.has(status)) {
        next.delete(status);
      } else {
        next.add(status);
      }
      return next;
    });
  }, []);

  const resetCommissionStatusFilter = useCallback((): void => {
    setCommissionStatusFilter(new Set(DEFAULT_COMMISSION_STATUS_FILTER));
  }, []);

  const resetBonusStatusFilter = useCallback((): void => {
    setBonusStatusFilter(new Set(DEFAULT_BONUS_STATUS_FILTER));
  }, []);

  const listQuery = useQuery({
    queryKey: [
      "commissions",
      "invoice-status",
      commissionStatusFilterKey,
      bonusStatusFilterKey,
      search,
      sortKey,
      sortDir,
      offset,
    ],
    queryFn: () =>
      listCommissionInvoiceStatuses({
        commissionStatuses,
        bonusStatuses,
        search: search.trim() === "" ? undefined : search.trim(),
        limit: PAGE_SIZE,
        offset,
        sortBy: sortKey,
        sortDir,
      }),
    ...commissionsStableListQueryOptions,
  });

  const rows = listQuery.data?.rows ?? [];
  const totalCount = listQuery.data?.total ?? 0;
  const commissionStatusCounts = listQuery.data?.commission_status_counts ?? {};
  const bonusStatusCounts = listQuery.data?.bonus_status_counts ?? {};

  const pageStart = totalCount === 0 ? 0 : offset + 1;
  const pageEnd = Math.min(offset + rows.length, totalCount);
  const hasPreviousPage = offset > 0;
  const hasNextPage = offset + PAGE_SIZE < totalCount;

  const hasActiveFilters =
    commissionStatusFilterKey !== statusFilterKey(DEFAULT_COMMISSION_STATUS_FILTER) ||
    bonusStatusFilterKey !== statusFilterKey(DEFAULT_BONUS_STATUS_FILTER) ||
    search.trim() !== "";
  const commissionFiltersAtDefault =
    commissionStatusFilterKey === statusFilterKey(DEFAULT_COMMISSION_STATUS_FILTER);
  const bonusFiltersAtDefault = bonusStatusFilterKey === statusFilterKey(DEFAULT_BONUS_STATUS_FILTER);

  function toggleSort(nextKey: CommissionInvoiceStatusSortKey): void {
    if (sortKey === nextKey) {
      setSortDir((current) => (current === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(nextKey);
    setSortDir(nextKey === "invoice_number" || nextKey === "end_user_name" ? "asc" : "desc");
  }

  return (
    <div className="space-y-6">
      <PageTitleWithHelp hoverHint="Track each GOSI sales invoice through base commission and tier-bonus workflows: customer payment, statement inclusion, and rep vendor payment.">
        Invoice commission status
      </PageTitleWithHelp>

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardBody className="space-y-4">
          <div>
            <Label htmlFor="invoice-search">Search</Label>
            <Input
              id="invoice-search"
              type="search"
              className="mt-1"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Invoice number, end user, or rep"
            />
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <StatusCountPills
              title="Commission status totals"
              options={COMMISSION_INVOICE_STATUS_OPTIONS}
              counts={commissionStatusCounts}
              selected={commissionStatusFilter}
              onToggle={toggleCommissionStatusFilter}
              onReset={resetCommissionStatusFilter}
              showReset={!commissionFiltersAtDefault}
            />
            <StatusCountPills
              title="Bonus status totals"
              options={BONUS_INVOICE_STATUS_OPTIONS}
              counts={bonusStatusCounts}
              selected={bonusStatusFilter}
              onToggle={toggleBonusStatusFilter}
              onReset={resetBonusStatusFilter}
              showReset={!bonusFiltersAtDefault}
            />
          </div>
        </CardBody>
      </Card>

      <HubDataTable
        title={
          totalCount === 0
            ? "Sales invoices"
            : `Showing ${pageStart.toLocaleString()}–${pageEnd.toLocaleString()} of ${totalCount.toLocaleString()} sales invoices`
        }
        columns={INVOICE_STATUS_COLUMNS}
        rows={rows}
        rowKey={(row) => row.invoice_mv_id}
        sortKey={sortKey}
        sortDir={sortDir}
        onSort={toggleSort}
        loading={listQuery.isPending && listQuery.data == null}
        loadingMessage="Loading invoices…"
        error={
          listQuery.isError
            ? listQuery.error instanceof Error
              ? listQuery.error.message
              : "Could not load invoice commission status."
            : null
        }
        empty="No GOSI invoices found."
        isFiltered={hasActiveFilters}
        filteredEmpty="No GOSI invoices match these filters."
        tableClassName="min-w-[72rem]"
      />

      {totalCount > PAGE_SIZE ? (
        <div className="flex items-center justify-end gap-2">
          <Button
            type="button"
            size="sm"
            variant="secondary"
            disabled={!hasPreviousPage}
            onClick={() => setOffset((value) => Math.max(0, value - PAGE_SIZE))}
          >
            Previous
          </Button>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            disabled={!hasNextPage}
            onClick={() => setOffset((value) => value + PAGE_SIZE)}
          >
            Next
          </Button>
        </div>
      ) : null}
    </div>
  );
}
